import React, { useEffect, useRef } from 'react';
import { ArrowRight, Camera } from 'lucide-react';
import gsap from 'gsap';
import { ONGOING_TOURS, UPCOMING_TOURS, GALLERY_IMAGES } from '../constants';

interface CTAProps {
  onNavigate: (page: string) => void;
}

const CTA: React.FC<CTAProps> = ({ onNavigate }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const sneakPeekRef = useRef<HTMLDivElement>(null);
  const gallerySneakPeekRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const tl = gsap.timeline();
            
            // Animate Top Section
            tl.fromTo(titleRef.current, 
              { y: 30, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
            )
            .fromTo(textRef.current,
              { y: 20, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
              "-=0.6"
            )
            .fromTo(buttonRef.current,
              { y: 20, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
              "-=0.6"
            );

            // Animate Bottom Section (Sneak Peek)
            if (sneakPeekRef.current) {
                const children = sneakPeekRef.current.children;
                gsap.fromTo(children,
                    { y: 30, opacity: 0 },
                    { y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: "power3.out", delay: 0.2 }
                );
            }

            // Animate Gallery Section
            if (gallerySneakPeekRef.current) {
                const children = gallerySneakPeekRef.current.children;
                gsap.fromTo(children,
                    { y: 30, opacity: 0 },
                    { y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: "power3.out", delay: 0.5 }
                );
            }

            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const tourSneakPeek = [
    ...ONGOING_TOURS.slice(0, 2),
    ...UPCOMING_TOURS.slice(0, 2)
  ];

  const gallerySneakPeek = GALLERY_IMAGES.slice(0, 4);

  const rotations = ['rotate-[-6deg]', 'rotate-[4deg]', 'rotate-[-3deg]', 'rotate-[8deg]'];
  const galleryRotations = ['rotate-[5deg]', 'rotate-[-4deg]', 'rotate-[6deg]', 'rotate-[-3deg]'];

  return (
    <section ref={containerRef} className="relative mt-16">
      
      {/* --- SECTION 1: SUBMISSION --- */}
      <div className="relative px-6 py-20 border-t border-slate-200 dark:border-slate-800 bg-gradient-to-b from-transparent to-slate-900/50">
        
        {/* Centered Logo Badge */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background-light dark:bg-background-dark px-4 z-10">
           <div className="font-bold tracking-[0.2em] text-[10px] text-primary uppercase text-center leading-tight font-display drop-shadow-[0_0_8px_rgba(249,115,22,0.8)]">
             STAR ROAD<br />
             <div className="flex justify-center my-3">
                <img 
                  src="https://i.imgur.com/blse8Ux.png" 
                  alt="Starroad Brand Mark" 
                  className="w-8 h-8 object-contain animate-[spin_6s_linear_infinite]"
                />
             </div>
             TOURING
           </div>
        </div>

        <div className="flex flex-col items-center text-center space-y-8">
            <h2 
                ref={titleRef}
                className="text-3xl font-bold tracking-tighter uppercase text-slate-900 dark:text-white font-display opacity-0"
            >
              Ready for the road?
            </h2>
            
            <p 
                ref={textRef}
                className="text-slate-500 dark:text-slate-400 text-sm max-w-[260px] font-mono leading-relaxed opacity-0"
            >
              The platform is now accepting submissions from artists. submit your EPK for a prompt review.
            </p>
            
            <button 
              ref={buttonRef}
              onClick={() => onNavigate('submission')}
              className="group relative bg-primary hover:bg-orange-600 text-white font-mono font-bold py-3 px-8 rounded-sm overflow-hidden transition-all duration-300 shadow-[0_0_15px_rgba(249,115,22,0.5)] hover:shadow-[0_0_25px_rgba(249,115,22,0.7)] hover:scale-105 active:scale-95 flex items-center justify-center opacity-0"
            >
              <span className="relative z-10 tracking-[0.2em] text-xs">SUBMIT</span>
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"></div>
            </button>
        </div>
      </div>

      {/* --- SECTION 2: TOUR PREVIEW --- */}
      <div className="px-6 py-16 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/20">
         <div ref={sneakPeekRef} className="w-full max-w-5xl mx-auto flex flex-col items-center">
            
            {/* Title */}
            <div className="flex items-center gap-4 mb-8 opacity-0">
                <div className="h-px w-8 bg-slate-400 dark:bg-slate-700"></div>
                <h3 className="text-xs font-mono font-bold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">
                    Tour Preview
                </h3>
                <div className="h-px w-8 bg-slate-400 dark:bg-slate-700"></div>
            </div>

            {/* Polaroid Stack */}
            <div className="flex justify-center items-center w-full mb-10 opacity-0 px-2 min-h-[220px]">
                {tourSneakPeek.map((item, idx) => {
                    const rotation = rotations[idx % rotations.length];
                    const margin = idx === 0 ? '' : '-ml-16 md:-ml-24';
                    return (
                        <div 
                            key={`${item.id}-${idx}`}
                            className={`group relative w-44 md:w-56 bg-slate-100 p-2 pb-8 shadow-xl cursor-pointer transition-all duration-300 ease-out hover:z-50 hover:scale-110 hover:rotate-0 ${rotation} ${margin}`}
                            onClick={() => onNavigate('tours')}
                        >
                             <div className="w-full aspect-square overflow-hidden bg-slate-200">
                                <img 
                                    src={item.image} 
                                    alt="Tour Preview" 
                                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                />
                             </div>
                        </div>
                    );
                })}
            </div>

            {/* CTA */}
            <button 
                onClick={() => onNavigate('tours')}
                className="opacity-0 group relative border border-slate-300 dark:border-slate-700 hover:border-primary text-slate-500 hover:text-primary font-mono font-bold text-[10px] py-3 px-8 uppercase tracking-[0.2em] transition-all hover:bg-primary/10 flex items-center gap-2"
            >
                View All Tours
                <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
            </button>

         </div>
      </div>

      {/* --- SECTION 3: GALLERY PREVIEW --- */}
      <div className="px-6 py-16 border-t border-b border-dashed border-slate-200 dark:border-slate-800 bg-transparent relative">
         <div ref={gallerySneakPeekRef} className="w-full max-w-5xl mx-auto flex flex-col items-center">
            
            {/* Title */}
            <div className="flex items-center gap-4 mb-8 opacity-0">
                <div className="h-px w-8 bg-primary/20"></div>
                <h3 className="text-xs font-mono font-bold uppercase tracking-[0.3em] text-primary/70">
                    Visual Archive Preview
                </h3>
                <div className="h-px w-8 bg-primary/20"></div>
            </div>

            {/* Polaroid Stack - Gallery Style */}
            <div className="flex justify-center items-center w-full mb-10 opacity-0 px-2 min-h-[220px]">
                {gallerySneakPeek.map((item, idx) => {
                    const rotation = galleryRotations[idx % galleryRotations.length];
                    const margin = idx === 0 ? '' : '-ml-16 md:-ml-24';
                    return (
                        <div 
                            key={`gal-${item.id}`}
                            className={`group relative w-44 md:w-56 bg-white dark:bg-slate-200 p-2 pb-2 shadow-2xl cursor-pointer transition-all duration-300 ease-out hover:z-50 hover:scale-110 hover:rotate-0 ${rotation} ${margin}`}
                            onClick={() => onNavigate('gallery')}
                        >
                             <div className="w-full aspect-square overflow-hidden bg-slate-900 grayscale hover:grayscale-0 transition-all duration-500">
                                <img 
                                    src={item.url} 
                                    alt={item.title} 
                                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100"
                                />
                             </div>
                        </div>
                    );
                })}
            </div>

            {/* CTA */}
            <button 
                onClick={() => onNavigate('gallery')}
                className="opacity-0 group relative border border-primary/30 hover:border-primary text-primary font-mono font-bold text-[10px] py-3 px-8 uppercase tracking-[0.2em] transition-all hover:bg-primary/5 flex items-center gap-2"
            >
                View Visual Logs
                <Camera size={12} className="group-hover:scale-110 transition-transform" />
            </button>

         </div>
      </div>

    </section>
  );
};

export default CTA;